import type { DeviceOrientation } from '../shared/protocol.js';

type OrientationPayload = Omit<DeviceOrientation, 'updatedAt'>;

interface OrientationEventWithCompass extends DeviceOrientationEvent {
  webkitCompassHeading?: number;
  webkitCompassAccuracy?: number;
}

interface PermissionAwareOrientationEvent {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

export type OrientationSensorStatus = 'idle' | 'requesting' | 'active' | 'unsupported' | 'denied';

export function normalizedHeading(
  alpha: number | null,
  webkitCompassHeading?: number
): number | null {
  const heading = Number.isFinite(webkitCompassHeading)
    ? (webkitCompassHeading as number)
    : Number.isFinite(alpha)
      ? 360 - (alpha as number)
      : null;
  if (heading === null) return null;
  return ((heading % 360) + 360) % 360;
}

export function relativeRotation(referenceHeading: number, heading: number): number {
  const delta = ((heading - referenceHeading + 540) % 360) - 180;
  return Math.abs(delta) < 0.05 ? 0 : delta;
}

export function orientationFromEvent(event: DeviceOrientationEvent): OrientationPayload | null {
  const compassEvent = event as OrientationEventWithCompass;
  const heading = normalizedHeading(event.alpha, compassEvent.webkitCompassHeading);
  if (heading === null || event.beta === null || event.gamma === null) return null;
  return {
    heading,
    pitch: Math.max(-180, Math.min(180, event.beta)),
    roll: Math.max(-90, Math.min(90, event.gamma)),
    absolute: event.absolute || Number.isFinite(compassEvent.webkitCompassHeading)
  };
}

export async function startOrientationSensor(
  onReading: (reading: OrientationPayload) => void
): Promise<() => void> {
  if (typeof window.DeviceOrientationEvent === 'undefined')
    throw new Error('Orientation sensors are unavailable in this browser.');

  const constructor = window.DeviceOrientationEvent as unknown as PermissionAwareOrientationEvent;
  if (constructor.requestPermission) {
    const permission = await constructor.requestPermission();
    if (permission !== 'granted') throw new Error('Orientation access was not allowed.');
  }

  const handleReading = (event: Event) => {
    const reading = orientationFromEvent(event as DeviceOrientationEvent);
    if (reading) onReading(reading);
  };
  window.addEventListener('deviceorientationabsolute', handleReading);
  window.addEventListener('deviceorientation', handleReading);
  return () => {
    window.removeEventListener('deviceorientationabsolute', handleReading);
    window.removeEventListener('deviceorientation', handleReading);
  };
}
